import type { Graphics } from 'pixi.js';
import {
  COLORS, STAT_FAMILY_COLOR, WEAPON_CATEGORY_COLOR, type StatFamily,
} from '@/config/colors';
import type { WeaponCategory } from '@/config/weapons';
import type { CardDef } from '@/upgrades/cards';
import { drawCombatGlyph } from './card-glyphs-combat';
import { drawSupportGlyph } from './card-glyphs-support';

/**
 * Procedural card icon glyphs — every card must be recognizable by
 * shape + color alone (no reading required). docs/03.
 * Shapes live in card-glyphs-combat.ts / card-glyphs-support.ts.
 */
export function drawCardIcon(g: Graphics, icon: string, accent: number): Graphics {
  g.clear();
  if (!drawCombatGlyph(g, icon, accent) && !drawSupportGlyph(g, icon, accent)) {
    g.circle(0, 0, 12).fill(accent);
  }
  return g;
}

/** Glyph → stat family (docs/03 W4). Weapon-category glyphs are separate. */
const ICON_FAMILY: Readonly<Record<string, StatFamily>> = {
  damage: 'power', critchance: 'power', critdamage: 'power', overcharge: 'power',
  barrel: 'power', rack: 'power',
  firerate: 'tempo', projspeed: 'tempo', range: 'tempo',
  supershot: 'super', superfan: 'super', energy: 'super',
  heart: 'defense', regen: 'defense', lifesteal: 'defense', shieldstart: 'defense',
  bulwark: 'defense', aegis: 'defense', salvager: 'defense', iframes: 'defense',
  speed: 'mobility', dashcd: 'mobility', ghostdash: 'mobility',
  magnet: 'economy', greed: 'economy', discount: 'economy', rerolldiscount: 'economy',
  luckychip: 'economy', cargo: 'economy',
};

/**
 * Tint of a glyph: its stat family colour, or the weapon category colour
 * for category glyphs (`cat-`/`dmg-`/`rate-<category>`).
 */
export function iconTint(icon: string): number {
  const dash = icon.indexOf('-');
  if (dash > 0) {
    const cat = icon.slice(dash + 1) as WeaponCategory;
    if (cat in WEAPON_CATEGORY_COLOR) return WEAPON_CATEGORY_COLOR[cat];
  }
  const family = ICON_FAMILY[icon];
  return family ? STAT_FAMILY_COLOR[family] : COLORS.statEconomy;
}

/** A card's accent = the tint of the stat it raises (trade-offs: the up side). */
export function cardTint(card: CardDef): number {
  return iconTint(card.icon);
}

export const RARITY_COLOR = {
  common: COLORS.rarityCommon,
  uncommon: COLORS.rarityUncommon,
  rare: COLORS.rarityRare,
  epic: COLORS.rarityEpic,
  legendary: COLORS.rarityLegendary,
  mythic: COLORS.rarityMythic,
} as const;
